import React, { useLayoutEffect, useRef, useState } from 'react';
import { ItemActionsIcon } from '../../../../../components/ItemActionsIcon/ItemActionsIcon';
import classes from './table-long-press-menu.module.scss';

type Props = {
  anchorStyle: React.CSSProperties;
  canvasScale?: number;
  compact?: boolean;
  onItemActions?: () => void;
  onPrintBill?: () => void;
  onTransfer?: () => void;
  transferDisabled?: boolean;
  onMerge?: () => void;
  onSplit?: () => void;
  onCancelTable?: () => void;
  cancelDisabled?: boolean;
  onTableHistory?: () => void;
  onClose: () => void;
};

// Small dark popover that appears below a long-pressed table on the floor map.
// Shows either onMerge OR onSplit — never both (merged tables show split, unmerged show merge).
// Transfer/cancel stay clickable when disabled so the parent handler can toast the reason.
const VIEWPORT_GUTTER = 8;

const triggerOnKeyboard = (event: React.KeyboardEvent<HTMLDivElement>, action: () => void) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

const TableLongPressMenuComponent: React.FC<Props> = ({ anchorStyle, canvasScale = 1, compact, onItemActions, onPrintBill, onTransfer, transferDisabled, onMerge, onSplit, onCancelTable, cancelDisabled, onTableHistory, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [viewportOffset, setViewportOffset] = useState({ x: 0, y: 0 });
  const [arrowLeft, setArrowLeft] = useState<number | null>(null);
  const viewportOffsetRef = useRef({ x: 0, y: 0 });
  const anchorTop = anchorStyle.top;
  const anchorLeft = anchorStyle.left;

  useLayoutEffect(() => {
    const keepInViewport = () => {
      const menu = menuRef.current;
      const rect = menu?.getBoundingClientRect();
      if (!menu || !rect) return;

      const coordinateScale = Math.max(canvasScale, Number.EPSILON);

      const horizontalViewportCorrection = rect.left < VIEWPORT_GUTTER
        ? VIEWPORT_GUTTER - rect.left
        : rect.right > window.innerWidth - VIEWPORT_GUTTER
          ? window.innerWidth - VIEWPORT_GUTTER - rect.right
          : 0;
      const verticalViewportCorrection = rect.top < VIEWPORT_GUTTER
        ? VIEWPORT_GUTTER - rect.top
        : rect.bottom > window.innerHeight - VIEWPORT_GUTTER
          ? window.innerHeight - VIEWPORT_GUTTER - rect.bottom
          : 0;
      const horizontalCorrection = horizontalViewportCorrection / coordinateScale;
      const verticalCorrection = verticalViewportCorrection / coordinateScale;

      if (horizontalCorrection || verticalCorrection) {
        const nextOffset = {
          x: viewportOffsetRef.current.x + horizontalCorrection,
          y: viewportOffsetRef.current.y + verticalCorrection,
        };
        viewportOffsetRef.current = nextOffset;
        setViewportOffset(nextOffset);
      }
    };

    const initialOffset = { x: 0, y: 0 };
    viewportOffsetRef.current = initialOffset;
    setViewportOffset(initialOffset);
    const frame = requestAnimationFrame(keepInViewport);
    window.addEventListener('resize', keepInViewport);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', keepInViewport);
    };
  }, [anchorLeft, anchorTop, canvasScale, compact]);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    const canvas = menu?.parentElement;
    const anchorX = typeof anchorLeft === 'number' ? anchorLeft : Number.parseFloat(String(anchorLeft));
    if (!menu || !canvas || !Number.isFinite(anchorX)) return;

    const canvasRect = canvas.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    // canvasInner scales from its right edge, so convert the canvas coordinate into screen space first.
    const anchorScreenX = canvasRect.right - (canvas.offsetWidth - anchorX) * canvasScale;
    const nextArrowLeft = anchorScreenX - menuRect.left;

    setArrowLeft((current) => current !== null && Math.abs(current - nextArrowLeft) < 0.5
      ? current
      : nextArrowLeft);
  }, [anchorLeft, canvasScale, compact, viewportOffset.x, viewportOffset.y]);

  return (
    <div
      ref={menuRef}
      className={`${classes.menu} ${compact ? classes.menuCompact : ''}`}
      style={{
        ...anchorStyle,
        '--menu-inverse-scale': `${1 / Math.max(canvasScale, Number.EPSILON)}`,
        '--menu-arrow-left': arrowLeft === null ? '50%' : `${arrowLeft}px`,
        '--menu-offset-x': `${viewportOffset.x}px`,
        '--menu-offset-y': `${viewportOffset.y}px`,
      } as React.CSSProperties}
      role="menu"
      data-testid="table-menu"
      onClick={onClose}
    >
      <div className={classes.arrow} />
      {onTransfer && (
        <div
          role="menuitem"
          tabIndex={0}
          aria-disabled={transferDisabled || undefined}
          className={`${classes.item} ${transferDisabled ? classes.itemDisabled : ''}`}
          onClick={onTransfer}
          data-testid="table-menu-transfer"
          onKeyDown={(event) => triggerOnKeyboard(event, onTransfer)}
        >
          <span className={classes.icon}>⇌</span>
          <span className={classes.label}>העברה</span>
        </div>
      )}
      {onTransfer && (onMerge || onSplit) && <div className={classes.divider} />}
      {onMerge && (
        <div
          role="menuitem"
          tabIndex={0}
          className={classes.item}
          onClick={onMerge}
          data-testid="table-menu-merge"
          onKeyDown={(event) => triggerOnKeyboard(event, onMerge)}
        >
          <span className={classes.icon}>
            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </span>
          <span className={classes.label}>מיזוג</span>
        </div>
      )}
      {onSplit && (
        <div
          role="menuitem"
          tabIndex={0}
          className={classes.item}
          onClick={onSplit}
          data-testid="table-menu-split"
          onKeyDown={(event) => triggerOnKeyboard(event, onSplit)}
        >
          <span className={classes.icon}>
            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              <line x1="4.5" y1="2" x2="2" y2="4.5" />
              <line x1="2" y1="2" x2="4.5" y2="4.5" />
              <line x1="19.5" y1="21.5" x2="22" y2="19" />
              <line x1="19.5" y1="19" x2="22" y2="21.5" />
            </svg>
          </span>
          <span className={classes.label}>פיצול</span>
        </div>
      )}
      {onCancelTable && (onTransfer || onMerge || onSplit) && <div className={classes.divider} />}
      {onCancelTable && (
        <div
          role="menuitem"
          tabIndex={0}
          aria-disabled={cancelDisabled || undefined}
          className={`${classes.item} ${cancelDisabled ? classes.itemDisabled : ''}`}
          onClick={onCancelTable}
          data-testid="table-menu-cancel"
          onKeyDown={(event) => triggerOnKeyboard(event, onCancelTable)}
        >
          <span className={classes.icon}>×</span>
          <span className={classes.label}>ביטול</span>
        </div>
      )}
      {onItemActions && (onTransfer || onMerge || onSplit || onCancelTable) && <div className={classes.divider} />}
      {onItemActions && (
        <div
          role="menuitem"
          tabIndex={0}
          className={classes.item}
          onClick={onItemActions}
          data-testid="table-menu-item-actions"
          onKeyDown={(event) => triggerOnKeyboard(event, onItemActions)}
        >
          <span className={classes.icon}>
            <ItemActionsIcon className={classes.iconImage} />
          </span>
          <span className={classes.label}>פעולות פריט</span>
        </div>
      )}
      {onTableHistory && (onTransfer || onMerge || onSplit || onCancelTable || onItemActions) && <div className={classes.divider} />}
      {onTableHistory && (
        <div
          role="menuitem"
          tabIndex={0}
          className={classes.item}
          onClick={onTableHistory}
          data-testid="table-menu-history"
          onKeyDown={(event) => triggerOnKeyboard(event, onTableHistory)}
        >
          <span className={classes.icon}>
            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 3v5h5" />
              <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
              <path d="M12 7v5l4 2" />
            </svg>
          </span>
          <span className={classes.label}>היסטוריית שולחן</span>
        </div>
      )}
      {onPrintBill && (onTransfer || onMerge || onSplit || onCancelTable || onTableHistory || onItemActions) && <div className={classes.divider} />}
      {onPrintBill && (
        <div
          role="menuitem"
          tabIndex={0}
          className={classes.item}
          onClick={onPrintBill}
          data-testid="table-menu-print-bill"
          onKeyDown={(event) => triggerOnKeyboard(event, onPrintBill)}
        >
          <span className={classes.icon}>
            <img src="/recieptprint.png" alt="" className={classes.iconImage} />
          </span>
          <span className={classes.label}>הדפס חשבון</span>
        </div>
      )}
    </div>
  );
};

export const TableLongPressMenu = React.memo(TableLongPressMenuComponent);
